import React, { useState, useEffect, useCallback } from "react";
import { DataGrid } from "@mui/x-data-grid";
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import { generateBalanceSheet } from "../../services/apiService";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { generateBalanceSheetPDF } from "../../services/apiService";
import "../../css/reports/balance-sheet.css";
const BalanceSheet = () => {
  const [balanceSheet, setBalanceSheet] = useState([]);
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const currentYear = new Date().getFullYear();

  const [selectedYear, setSelectedYear] = useState(currentYear);

  // Generate an array of years (e.g., 2000 to 2030)
  const startYear = 2020;

  const years = Array.from(
    { length: currentYear - startYear + 1 },
    (_, index) => startYear + index,
  );

  const handleYearChange = (event) => {
    const year = parseInt(event.target.value);
    setSelectedYear(year);
  };

  const fetchReportData = useCallback(async (year) => {
    setIsLoading(true);
    try {
      const response = await generateBalanceSheet({ year: year });
      console.log("API Response format:", response?.balanceSheet);
      if (response.status == true) {
        setBalanceSheet(response?.balanceSheet);
      }
    } catch (error) {
      console.error("Error fetching report data:", error);
      setRows([]);
      setColumns([]);
    } finally {
      setIsLoading(false);
    }
  }, []); // Keep empty deps - function is stable

  const balanceSheetRows = balanceSheet
    ? Object.entries(balanceSheet).map(([key, value], index) => ({
        id: index,
        accountName: key,
        amount: `OMR ${Number(value).toLocaleString()}`,
      }))
    : [];

  const balanceSheetColumns = [
    {
      field: "accountName",
      headerName: "Account Name",
      flex: 3,
      renderCell: (params) => <strong>{params.value}</strong>,
    },
    {
      field: "amount",
      headerName: "Amount",
      flex: 2,
    },
  ];

  // ONLY ONE useEffect for initial load + year change
  useEffect(() => {
    fetchReportData(selectedYear);
  }, [selectedYear]); // Remove fetchReportData from deps - useCallback makes it stable

  const handleExportExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(`Balance Sheet ${selectedYear}`);

    // Column setup
    worksheet.columns = [
      { key: "particular", width: 45 },
      { key: "amount", width: 20 },
    ];

    const reportDate = new Date().toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });

    /* =========================
     HEADER SECTION
  ========================== */

    worksheet.addRow(["TRANS OCEAN MARITIME SERVICES LLC"]);
    worksheet.addRow(["Falaj Al Qabail Sohar, Sultanate Of Oman"]);
    worksheet.addRow([]); // spacer

    worksheet.addRow([
      `Statement of Financial Position as at December 31, ${selectedYear}`,
    ]);
    worksheet.addRow(["(In Omani Riyal)"]);
    worksheet.addRow([]); // spacer

    // Header styling
    for (let i = 1; i <= 5; i++) {
      const row = worksheet.getRow(i);
      row.font = { bold: i === 1 || i === 3, size: 12 };
      row.alignment = { horizontal: "left", vertical: "middle" };
      worksheet.mergeCells(i, 1, i, 2);
    }

    /* =========================
     TABLE DATA
  ========================== */

    const addSection = (title) => {
      const row = worksheet.addRow([title, ""]);
      row.font = { bold: true };
      // applyBorder(row);
    };

    const addRow = (label, value, isBold = false) => {
      const isEmpty = value === null || value === undefined || value === "";

      const row = worksheet.addRow([
        label,
        isEmpty ? "" : Number(Number(value).toFixed(3)),
      ]);

      row.font = { bold: isBold };
      row.getCell(1).alignment = { horizontal: "left" };
      row.getCell(2).alignment = { horizontal: "right" };

      if (!isEmpty) {
        row.getCell(2).numFmt = "0.000";
      }

      // applyBorder(row);
    };

    const applyBorder = (row) => {
      [1, 2].forEach((col) => {
        row.getCell(col).border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });
    };

    /* =========================
     ASSETS
  ========================== */

    addSection("Assets");
    addSection("");
    addSection("Fixed Assets");
    addRow("Property and equipment", balanceSheet.fixedAsset);
    addSection("");
    addSection("Current Assets");
    addRow("Accounts Receivables", balanceSheet.accountsReceivables);
    addRow("Bank Accounts", balanceSheet.totalBankAsset);
    addRow("Cash and cash equivalents", balanceSheet.totalCashAsset);
    addRow("Prepaid Expenses", balanceSheet.prepaidExpense);
    addRow("Deposits", balanceSheet.totalDeposits);
    addSection("");

    addRow("Total Assets", balanceSheet.totalAsset, true);
    addSection("");

    /* =========================
     EQUITY & LIABILITIES
  ========================== */

    addSection("Equity and Liabilities");
    addSection("");

    addSection("Equity");
    addRow("Capital Account", balanceSheet.totalCapital);
    addRow("Profit and Loss account balance", balanceSheet.profitAndLoss);
    addSection("");

    addRow("Provision for Gratuity", balanceSheet.gratuityTotal);
    addSection("");

    addSection("Liabilities");
    addRow("Accounts Payables", balanceSheet["Accounts Payables"]);
    addRow("Electricity Payables", balanceSheet["Electricity Payables"]);
    addRow(
      "Telephone & Internet Payables",
      balanceSheet["Telephone & Internet Payables"],
    );
    addSection("");

    addRow(
      "Total Equity and Liabilities",
      balanceSheet.totalEquityLiability,
      true,
    );
    addSection("");

    addSection("");

    addRow("Notes", "");
    addRow("Assets = liabilities + equity", "");
    addRow("Equity = assets – liabilities", "");

    /* =========================
     ROW HEIGHT
  ========================== */

    worksheet.eachRow({ includeEmpty: false }, (row) => {
      row.height = 22;
    });

    /* =========================
     DOWNLOAD
  ========================== */

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    saveAs(blob, `Balance Sheet ${selectedYear}.xlsx`);
  };

  const getPDF = async () => {
    const payload = { year: selectedYear };
    console.log(payload, "payload_getReport");
    try {
      const response = await generateBalanceSheetPDF(payload);
      console.log("getPettyCashReport", response);

      if (response?.pdfPath) {
        const pdfUrl = `${process.env.REACT_APP_ASSET_URL}${response.pdfPath}`;
        const pdfResponse = await fetch(pdfUrl);
        const pdfBlob = await pdfResponse.blob();
        const pdfBlobUrl = URL.createObjectURL(pdfBlob);
        const link = document.createElement("a");
        link.href = pdfBlobUrl;
        link.setAttribute("download", "Balance Sheet.pdf");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(pdfBlobUrl);
      }
    } catch (error) {
      console.error("Failed to fetch quotations:", error);
    }
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height={400}
        flexDirection="column"
        gap={2}
      >
        <CircularProgress />
        <Typography>Loading report data...</Typography>
      </Box>
    );
  }

  const NoRowsOverlay = () => (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100%",
        color: "gray",
      }}
    >
      <Typography>No reports are available for the specified terms</Typography>
    </Box>
  );
  const reportDate = new Date().toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

  return (
    <>
      <style jsx>{`
        .responsive-table-container {
          height: 70vh;
          max-height: 700px;
          width: 100%;
          overflow: auto;
          border: 1px solid #dee2e6;
          border-radius: 8px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }
        .table-inner {
          min-width: 1450px;
          height: 100%;
        }
        .responsive-table-container::-webkit-scrollbar {
          height: 12px;
          width: 12px;
        }
        .responsive-table-container::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 6px;
        }
        .responsive-table-container::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 6px;
        }
        .responsive-table-container::-webkit-scrollbar-thumb:hover {
          background: #a8a8a8;
        }
        .bold-text {
          font-weight: 600;
        }
        .fy-total {
          font-weight: 700 !important;
          color: #1976d2;
        }
        @media (max-width: 1400px) {
          .table-inner {
            min-width: 1350px;
          }
        }
        @media (max-width: 1200px) {
          .table-inner {
            min-width: 1250px;
          }
        }
      `}</style>

      <div className="row d-flex align-items-center justify-content-between m-3">
        <div className="col-lg-3 col-md-4 col-sm-6">
          <label htmlFor="" className="form-label">
            Select year
          </label>
          <select
            name="year"
            className="form-select vesselbox"
            aria-label="Default select example"
            onChange={handleYearChange}
            value={selectedYear}
          >
            {years.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
        <div className="col-lg-3 col-md-4 col-sm-6">
          <div className="d-flex gap-2 justify-content-end">
            <button
              className="btn btn-info filbtnjob new_width"
              onClick={handleExportExcel}
            >
              Download Excel
            </button>
            <button
              className="btn btn-info filbtnjob new_width"
              onClick={getPDF}
            >
              Download PDF
            </button>
          </div>
        </div>
      </div>

      <div className="balance-sheet-main">
        <div className="bs-header">
          <div className="bs-title">TRANS OCEAN MARITIME SERVICES LLC</div>
          <div className="bs-subtitle">
            Falaj Al Qabail Sohar, Sultanate Of Oman
          </div>

          <div className="bs-statement">
            Statement of Financial Position as at December 31, {selectedYear}
          </div>

          <div className="bs-currency">(In Omani Riyal)</div>
        </div>

        <table className="bs-table">
          <tbody>
            <tr className="bs-section">
              <td colSpan="2">Fixed Assets</td>
            </tr>
            <tr>
              <td>Property and equipment</td>
              <td className="bs-amount">{balanceSheet.fixedAsset}</td>
            </tr>

            <tr className="bs-section">
              <td colSpan="2">Current Assets</td>
            </tr>
            <tr>
              <td>Accounts Receivables</td>
              <td className="bs-amount">{balanceSheet.accountsReceivables}</td>
            </tr>
            <tr>
              <td>Bank Accounts</td>
              <td className="bs-amount">{balanceSheet.totalBankAsset}</td>
            </tr>
            <tr>
              <td>Cash and cash equivalents</td>
              <td className="bs-amount">{balanceSheet.totalCashAsset}</td>
            </tr>
            <tr>
              <td>Prepaid Expenses</td>
              <td className="bs-amount">{balanceSheet.prepaidExpense}</td>
            </tr>
            <tr>
              <td>Deposits</td>
              <td className="bs-amount">{balanceSheet.totalDeposits}</td>
            </tr>

            <tr className="bs-total">
              <td>Total Assets</td>
              <td className="bs-amount">{balanceSheet.totalAsset}</td>
            </tr>

            <tr className="bs-section">
              <td colSpan="2">Equity and Liabilities</td>
            </tr>

            <tr className="bs-subsection">
              <td colSpan="2">Equity</td>
            </tr>
            <tr>
              <td>Capital Account</td>
              <td className="bs-amount">{balanceSheet.totalCapital}</td>
            </tr>
            <tr>
              <td>Profit and Loss account balance</td>
              <td className="bs-amount">{balanceSheet.profitAndLoss}</td>
            </tr>
            <tr>
              <td>Provision for Gratuity</td>
              <td className="bs-amount">{balanceSheet.gratuityTotal}</td>
            </tr>

            <tr className="bs-subsection">
              <td colSpan="2">Liabilities</td>
            </tr>
            <tr>
              <td>Accounts Payables</td>
              <td className="bs-amount">{balanceSheet["Accounts Payables"]}</td>
            </tr>
            <tr>
              <td>Electricity Payables</td>
              <td className="bs-amount">
                {balanceSheet["Electricity Payables"]}
              </td>
            </tr>
            <tr>
              <td>Telephone & Internet Payables</td>
              <td className="bs-amount">
                {balanceSheet["Telephone & Internet Payables"]}
              </td>
            </tr>

            <tr className="bs-total">
              <td>Total Equity and Liabilities</td>
              <td className="bs-amount">{balanceSheet.totalEquityLiability}</td>
            </tr>

            <tr>
              <td>
                <div className="bs-notes">
                  <strong>Notes:</strong>
                </div>
              </td>
              <td className="bs-amount"></td>
            </tr>
            <tr>
              <td>
                {" "}
                <p>Assets = liabilities + equity</p>
              </td>
              <td className="bs-amount"></td>
            </tr>
            <tr>
              <td>
                {" "}
                <p>Equity = assets – liabilities</p>
              </td>
              <td className="bs-amount"></td>
            </tr>
          </tbody>
        </table>
        {/* <div className="bs-notes">
          <strong>Notes:</strong>
          <ul>
            <li>Assets = liabilities + equity</li>
            <li>Equity = assets – liabilities</li>
          </ul>
        </div> */}
      </div>
    </>
  );
};

export default BalanceSheet;
